import { ApiMsgTypes } from "@/api/AppApi/common-interfaces/IResultStamp";
import { useCheckAccountExist, useSignIn } from "@/api/AppApi/services/auth";
import { LoginMethodTypes } from "@/api/AppApi/services/company/interfaces/ICompany";
import { isSignedInAtom } from "@/hooks/authAtoms";
import { useAtom } from "jotai";
import { useRouter } from "@/app/components/lib/navigation/useRouter";
import { useEffect, useState } from "react";
import { Col, Row } from "@/utils/bootstrap";
import { useForm } from "react-hook-form";
import AuthButton from "../auth-common/AuthButton";
import SMSCodeBtn from "./sms-code-btn/SMSCodeBtn";
import {
  storeAuthTokens,
  deleteTokens,
} from "@/utils/functions/cookieOperations";
import * as ls from "@/utils/functions/app-local-storage";
import { otpLoginQuery } from "./OtpSignIn";
import {
  FieldTypes,
  IField,
} from "@/api/AppApi/services/builder/interfaces/interfaces";
import {
  createCustomFields,
  defaultValues,
} from "@/app/components/common-form-builder/form-builder-helpers/form-functions";
import CommonFormBuilder from "@/app/components/common-form-builder/CommonFormBuilder";
import BaseOption from "@/app/components/common-filter-builder/common-filters-components/base-checkbox/BaseOption";
import { PagesPaths } from "@/utils/functions/pages-paths";
import { useGetCompanyQuery } from "@/api/AppApi/services/company";
import { IsTopWinner } from "@/utils/functions/global-constants";
import { useSearchParams } from "next/navigation";
import { useTranslation } from "@/hooks/localization/useTranslations";
import { IGTagUserData } from "@/utils/helpers/pixel-helpers/hooks/useEventTrackers/helpers/interfaces";
import useEventTrackers from "@/utils/helpers/pixel-helpers/hooks/useEventTrackers/useEventTrackers";

interface Props {
  registerBy?: LoginMethodTypes;
  isFromCheckout?: boolean;
}
enum FNames {
  mobile = "mobile",
  mobilePassword = "mobilePassword",
  email = "email",
  emailPassword = "emailPassword",
}
const SignInForm: React.FC<Props> = ({ registerBy, isFromCheckout }) => {
  const router = useRouter();
  const searchParams = useSearchParams();
  const [, setSignedIn] = useAtom(isSignedInAtom);
  const { userLoginEventTracher } = useEventTrackers();
  const getCompany = useGetCompanyQuery();
  const company = getCompany?.data?.result;

  const isSchool = company?.theme?.settings?.features?.school;
  const hookForm = useForm<any>();
  const [fields, setFields] = useState<IField[]>();
  const isEmail = registerBy === LoginMethodTypes.by_email;
  const fieldsToView = isEmail
    ? fields?.filter((field) => field.configurations.key?.includes("email"))
    : fields?.filter((field) => field.configurations.key?.includes("mobile"));
  const t = useTranslation();
  const { handleSubmit, setError, getValues } = hookForm;
  const [remeberMe, setRemeberMe] = useState(true);
  const isFromCart = searchParams.get("fromCart");
  const signInPost = useSignIn({
    onSuccess: (res) => {
      if (res?.message?.type === ApiMsgTypes.success) {
        const { user } = res?.result ?? {};
        storeAuthTokens(res.result?.access_token!, res.result?.refresh_token!);
        setSignedIn(true);
        const userData: IGTagUserData = {
          user_id: user?.id,
          name: user?.name,
          email: user?.email,
          phone: user?.mobile,
        };
        userLoginEventTracher(true, { ...userData });
        if (remeberMe) {
          if (isEmail) {
            const email = getValues(FNames.email);
            const password = getValues(FNames.emailPassword);
            ls.set(ls.Keys.userSignInEmail, { password, email });
          } else {
            const mobile = getValues(FNames.mobile);
            const password = getValues(FNames.mobilePassword);
            ls.set(ls.Keys.userSignInMobile, {
              password,
              mobile: { number: mobile.number, dial_code_country: mobile.dial },
            });
          }
        }
        const originalPath = searchParams.get("original"); //check if added in middleware
        if (!!originalPath) {
          const params = new URLSearchParams(
            searchParams as unknown as URLSearchParams,
          );
          params.delete("original");
          router.replace(originalPath + "?" + params?.toString());
        } else
          router.push(
            isFromCheckout
              ? PagesPaths.userCheckout
              : isFromCart
                ? PagesPaths.cart
                : IsTopWinner(company!)
                  ? PagesPaths.agentsForms
                  : isSchool
                    ? PagesPaths.students
                    : "/",
          );
      }
    },
    onError: (error) => {
      if (error.response?.status === 400)
        setError(isEmail ? FNames.emailPassword : FNames.mobilePassword, {
          message: error.response?.data.message.text,
        });
    },
  });
  const checkAccountExist = useCheckAccountExist({
    onSuccess: (res) => {
      if (res?.message?.type === ApiMsgTypes.success) {
        const usernameField = getValues(isEmail ? FNames.email : FNames.mobile);
        const username = isEmail
          ? usernameField
          : JSON.stringify({
              number: usernameField.number,
              country: usernameField.dial?.iso2,
            });
        router.push(otpLoginQuery(username));
      }
    },
    onError: (error) => {
      if (error.response?.status === 404)
        setError(isEmail ? FNames.email : FNames.mobile, {
          message: error.response?.data.message.text,
        });
    },
  });
  const onSubmit = (data: any) => {
    deleteTokens();
    signInPost.mutate(
      isEmail
        ? {
            password: data.emailPassword,
            username: data.email,
          }
        : {
            password: data.mobilePassword,
            username: {
              number: data.mobile?.number,
              country: data.mobile?.dial?.iso2?.toUpperCase(),
            },
            // data.mobile?.dial?.dial_code + data.mobile?.number,
          },
    );
  };
  const handleInnerSubmit = (event: React.BaseSyntheticEvent) => {
    event.stopPropagation();
    handleSubmit(onSubmit)(event);
  };
  const handelSMSLogin = () => {
    const data = getValues();
    checkAccountExist.mutate(
      isEmail
        ? data.email
        : {
            number: data.mobile?.number,
            country: data.mobile?.dial?.iso2?.toUpperCase(),
          },
    );
  };
  useEffect(() => {
    if (getCompany.isLoading) return;
    const emailSignedIn = ls.get(ls.Keys.userSignInEmail);
    const mobileSignedIn = ls.get(ls.Keys.userSignInMobile);
    const email = emailSignedIn ? emailSignedIn.email : null;
    const passwordEmail = emailSignedIn ? emailSignedIn.password : null;
    const mobile = mobileSignedIn ? mobileSignedIn.mobile : null;
    const passwordMobile = mobileSignedIn ? mobileSignedIn.password : null;
    const fields = createCustomFields([
      {
        key: FNames.email,
        type: FieldTypes.email,
        title: t.e_mail,
        is_required: true,
        value: email,
      },
      {
        key: FNames.emailPassword,
        type: FieldTypes.password,
        title: t.password,
        is_required: true,
        value: passwordEmail,
      },
      {
        key: FNames.mobile,
        type: FieldTypes.phone,
        title: t.phone,
        is_required: true,
        value: mobile,
      },
      {
        key: FNames.mobilePassword,
        type: FieldTypes.password,
        title: t.password,
        is_required: true,
        value: passwordMobile,
      },
    ]);
    hookForm.reset(defaultValues(fields));
    setFields(fields);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getCompany.isLoading]);

  return (
    <form onSubmit={handleInnerSubmit} className="auth-form">
      {fieldsToView?.map((field) => (
        <CommonFormBuilder
          key={field.configurations.key}
          form={hookForm}
          field={field}
        />
      ))}
      <Row style={{ margin: "16px 0 0" }}>
        <Col className="p-0 col-auto">
          <BaseOption
            selected={remeberMe}
            toggleOption={() => setRemeberMe(!remeberMe)}
            is_multi_select
            label={t.remember_me}
          />
        </Col>
        <Col />
        <Col
          className="auth-underline-black p-0 col-auto"
          onClick={() => router.push(PagesPaths.forget_password)}
        >
          {t.forgot_password}
        </Col>
      </Row>
      {/* disabled={!isEmail && isEmptyPassword} */}
      <AuthButton isLoading={signInPost.isLoading}>{t.sign_in}</AuthButton>
      <SMSCodeBtn
        isLoading={checkAccountExist.isLoading}
        onClick={() => handelSMSLogin()}
      >
        {isEmail ? t.login_with_mail_code : t.login_with_sms_code}
      </SMSCodeBtn>
    </form>
  );
};

export default SignInForm;
